<script setup>
import { ref, computed, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { X, Send, CheckCircle2, AlertCircle } from 'lucide-vue-next'
import axios from 'axios'
import { Button } from '@/Components/ui/button'
import { useScrollLock } from '@/composables/useScrollLock'

/**
 * Ask a question about a tutor before subscribing.
 *
 * There is no direct tutor messaging before a subscription — a conversation is
 * only created for an active Silver or Gold subscription — so a "Send message"
 * button on a public profile could not do what it said. Rather than invent a
 * feature or leave a dead button, this posts through the support channel that
 * already works: it records a contact, emails the team and acknowledges the
 * sender. The wording says exactly that.
 */
const props = defineProps({
  open: { type: Boolean, default: false },
  tutorName: { type: String, required: true },
  tutorSlug: { type: String, required: true },
})

const emit = defineEmits(['close'])

useScrollLock(() => props.open)

const page = usePage()
const user = computed(() => page.props.auth?.user)
const isGuest = computed(() => !user.value)

const name = ref('')
const email = ref('')
const message = ref('')
const sending = ref(false)
const sent = ref(false)
const error = ref('')

const canSubmit = computed(() => {
  if (sending.value || message.value.trim().length < 10) return false
  if (isGuest.value && (!name.value.trim() || !email.value.trim())) return false
  return true
})

const submit = async () => {
  if (!canSubmit.value) return

  sending.value = true
  error.value = ''

  try {
    await axios.post('/support', {
      message: message.value.trim(),
      tutor: props.tutorSlug,
      ...(isGuest.value ? { name: name.value.trim(), email: email.value.trim() } : {}),
    })
    sent.value = true
    message.value = ''
  } catch (e) {
    error.value = e.response?.data?.message || 'We could not send that. Please try again.'
  } finally {
    sending.value = false
  }
}

watch(() => props.open, (open) => {
  if (open) {
    sent.value = false
    error.value = ''
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-150"
      enter-from-class="opacity-0" leave-to-class="opacity-0"
    >
      <div v-if="open" class="fixed inset-0 z-90 bg-ink/50" @click="emit('close')"></div>
    </Transition>

    <Transition
      enter-active-class="transition duration-200 ease-brand"
      leave-active-class="transition duration-150 ease-in"
      enter-from-class="opacity-0 translate-y-3" leave-to-class="opacity-0 translate-y-3"
    >
      <div
        v-if="open"
        class="fixed left-1/2 top-1/2 z-100 w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-line bg-surface shadow-lg"
        role="dialog"
        aria-modal="true"
        :aria-label="`Message ${tutorName}`"
      >
        <div class="flex items-start justify-between gap-4 border-b border-line p-5">
          <div>
            <h2 class="text-lg">Message {{ tutorName }}</h2>
            <p class="mt-1 text-sm text-muted">
              Your message goes to the Bindle team, who will answer it or put you in touch.
            </p>
          </div>
          <button type="button" class="text-subtle hover:text-ink" aria-label="Close" @click="emit('close')">
            <X class="size-5" />
          </button>
        </div>

        <div v-if="sent" class="p-8 text-center">
          <CheckCircle2 class="mx-auto size-10 text-success" />
          <h3 class="mt-4 text-lg">Question sent</h3>
          <p class="mt-2 text-sm text-muted">
            We have emailed you a copy. You will normally hear back the same working day.
          </p>
          <Button class="mt-6" variant="secondary" @click="emit('close')">Close</Button>
        </div>

        <form v-else class="space-y-4 p-5" @submit.prevent="submit">
          <template v-if="isGuest">
            <div>
              <label class="mb-1.5 block text-sm font-semibold text-ink" for="enq-name">Your name</label>
              <input
                id="enq-name"
                v-model="name"
                type="text"
                class="h-11 w-full rounded-md border border-line-strong bg-surface px-3 text-[15px] text-ink focus:border-brand focus:outline-none focus:ring-3 focus:ring-brand/20"
              />
            </div>
            <div>
              <label class="mb-1.5 block text-sm font-semibold text-ink" for="enq-email">Your email</label>
              <input
                id="enq-email"
                v-model="email"
                type="email"
                class="h-11 w-full rounded-md border border-line-strong bg-surface px-3 text-[15px] text-ink focus:border-brand focus:outline-none focus:ring-3 focus:ring-brand/20"
              />
            </div>
          </template>

          <div>
            <label class="mb-1.5 block text-sm font-semibold text-ink" for="enq-message">Your message</label>
            <textarea
              id="enq-message"
              v-model="message"
              rows="4"
              :placeholder="`What would ${tutorName} do for a student struggling with…`"
              class="w-full rounded-md border border-line-strong bg-surface p-3 text-[15px] text-ink placeholder:text-subtle focus:border-brand focus:outline-none focus:ring-3 focus:ring-brand/20"
            ></textarea>
          </div>

          <p v-if="error" class="flex items-start gap-2 text-sm text-danger">
            <AlertCircle class="mt-0.5 size-4 shrink-0" /> {{ error }}
          </p>

          <Button type="submit" class="w-full" size="lg" :disabled="!canSubmit">
            <Send class="size-4" />
            {{ sending ? 'Sending…' : 'Send message' }}
          </Button>
        </form>
      </div>
    </Transition>
  </Teleport>
</template>
